import { type Ref, computed, reactive, toRefs } from 'vue'

import { Theme } from '@/enums/common.ts'

const layoutConfig = reactive({
    theme: Theme.Light as Theme,
    darkTheme: false,
    activeMenuItem: ''
})

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    activeMenuItem: ''
})

const prefersDarkQuery = () => window.matchMedia('(prefers-color-scheme: dark)')

let systemThemeListenerAttached = false

export const useLayout = () => {
    const applyTheme = (theme: Theme) => {
        layoutConfig.theme = theme
        layoutConfig.darkTheme = theme === Theme.Dark || (theme === Theme.Auto && prefersDarkQuery().matches)
        document.documentElement.classList.toggle('dark', layoutConfig.darkTheme)
    }

    const changeThemeSettings = (theme: Theme) => {
        localStorage.setItem('theme', theme)
        applyTheme(theme)
    }

    const setTheme = () => {
        const storedTheme = localStorage.getItem('theme')
        const theme = Object.values(Theme).includes(storedTheme as Theme) ? storedTheme as Theme : Theme.Light

        localStorage.setItem('theme', theme)
        applyTheme(theme)

        // with Theme.Auto the page has to follow the operating system while it stays open
        if (!systemThemeListenerAttached) {
            prefersDarkQuery().addEventListener('change', () => {
                if (layoutConfig.theme === Theme.Auto) applyTheme(Theme.Auto)
            })
            systemThemeListenerAttached = true
        }
    }

    const setActiveMenuItem = (item: Ref<string> | string) => {
        if (typeof item === 'string') {
            layoutConfig.activeMenuItem = item
        } else {
            layoutConfig.activeMenuItem = item.value
        }
    }

    const onMenuToggle = () => {
        if (window.innerWidth > 1024) { // tailwind lg
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive
        }
    }

    const isSidebarActive = computed(() => layoutState.staticMenuMobileActive)

    return {
        layoutConfig: toRefs(layoutConfig),
        layoutState: toRefs(layoutState),
        changeThemeSettings,
        setTheme,
        onMenuToggle,
        isSidebarActive,
        setActiveMenuItem
    }
}

